import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  ArrowLeft,
  AlertCircle,
  RefreshCcw,
  HelpCircle,
  Phone,
} from "lucide-react";

const ErrorPage = ({ errorCode = "404", errorMessage = "Page Not Found" }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-none sm:rounded-2xl shadow-none sm:shadow-lg overflow-x-hidden">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[50%] sm:w-[60%] sm:h-[60%] bg-gradient-to-br from-primary-200/30 to-secondary-200/30 dark:from-primary-900/30 dark:to-secondary-900/30 rounded-full blur-3xl" />
      </div>

      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-12 sm:py-20">
        <div className="max-w-2xl w-full text-center">
          {/* Hero Illustration */}
          <div className="relative mb-10 sm:mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Large error code – scales down on mobile */}
              <h1 className="text-[7.5rem] xs:text-[9rem] sm:text-[14rem] md:text-[18rem] font-black leading-none tracking-[-0.05em] text-gray-200 dark:text-gray-800 select-none">
                {errorCode}
              </h1>

              {/* Floating Glass Card */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <motion.div
                  animate={{
                    y: [0, -12, 0],
                    rotate: [0, 2, -2, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border border-white/50 dark:border-gray-700/50 p-6 sm:p-10 relative"
                >
                  <div className="absolute -top-3 -right-3 sm:-top-5 sm:-right-5 w-10 h-10 sm:w-14 sm:h-14 bg-red-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                    <AlertCircle
                      size={20}
                      className="text-white sm:w-7 sm:h-7"
                    />
                  </div>

                  <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 mx-auto bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-inner">
                    <HelpCircle
                      size={40}
                      className="text-white opacity-90 sm:w-[68px] sm:h-[68px]"
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="px-1"
          >
            <h2 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-3 sm:mb-4">
              Oops! {errorMessage}
            </h2>

            <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-8 sm:mb-12 leading-relaxed">
              The page you're looking for might have been moved, deleted, or is
              temporarily unavailable.
            </p>

            {/* Primary Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 w-full max-w-md sm:max-w-none mx-auto">
              <button
                onClick={() => navigate(-1)}
                className="group inline-flex items-center justify-center gap-2.5 sm:gap-3 w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 rounded-2xl sm:rounded-3xl font-semibold text-sm sm:text-base text-gray-700 dark:text-gray-300 transition-all hover:shadow-md active:scale-[0.985]"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform shrink-0" />
                Go Back
              </button>

              <Link
                to="/"
                className="group inline-flex items-center justify-center gap-2.5 sm:gap-3 w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold text-sm sm:text-base rounded-2xl sm:rounded-3xl transition-all active:scale-[0.985]"
              >
                <Home className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform shrink-0" />
                Back to Homepage
              </Link>
            </div>

            {/* Secondary Links */}
            <div className="flex flex-col xs:flex-row flex-wrap justify-center items-center gap-x-6 sm:gap-x-8 gap-y-3 sm:gap-y-4 text-sm">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <RefreshCcw size={16} className="sm:w-[18px] sm:h-[18px]" />
                Refresh Page
              </button>

              <Link
                to="/contact"
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <Phone size={16} className="sm:w-[18px] sm:h-[18px]" />
                Contact Support
              </Link>

              <Link
                to="/support"
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <HelpCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                Visit Support Center
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
